import React from "react";

export const CONTEXT_LABELS = Object.freeze({
  "marine_sea_surface_temperature": "water_temperature",
  "freshwater_temperature": "water_temperature",
  "marine_sea_level_height": "sea_level",
  "marine_wave_height": "wave_height",
  "marine_wave_period": "wave_period",
  "marine_wave_direction": "wave_direction",
  "river_discharge": "river_flow",
  "river_level": "river_level",
  "river_flow_percentile": "flow_percentile",
  "river_flow_regime": "flow_regime",
  "river_flow_trend": "flow_trend",
  "wind_speed": "wind",
  "wind_gust": "gust",
  "rainfall_amount": "rain",
  "rainfall_probability": "rain_probability",
  "air_temperature": "air_temperature",
  "feels_like_temperature": "feels_like",
  "wind_direction": "wind_direction",
  "humidity": "humidity",
  "uv_index": "uv",
  "moon_phase": "moon",
  "moon_illumination": "moon_illumination",
  "daylight": "daylight",
  "local_time": "local_time",
  "season": "season"
});

const UNITS = { celsius: "°C", kph: "km/h", millimetres: "mm", metres: "m",
  metres_msl: "m MSL", seconds: "s", degrees_from: "°", degrees: "°",
  cubic_metres_per_second: "m³/s", "m3/s": "m³/s", "mASD": "m",
  metres_provider_datum: "m", percent: "%", fraction: "%", index: "" };
const VALUE_KEYS = new Set(["rising", "stable", "falling", "unusually_low", "below_typical",
  "typical_range", "above_typical", "unusually_high", "daylight", "night"]);

function displayValue(item, locale, t) {
  if (!["available", "historical"].includes(item.status) || item.value === null || item.value === undefined)
    return t("fishing_today_general_unavailable");
  if (typeof item.value === "number" && Number.isFinite(item.value)) {
    const value = item.units === "fraction" ? item.value * 100 : item.value;
    const unit = UNITS[item.units];
    if (typeof unit !== "string") return t("fishing_today_general_unavailable");
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)} ${unit}`.trim();
  }
  if (VALUE_KEYS.has(item.value)) return t(`fishing_today_general_value_${item.value}`);
  if (item.id === "local_time" && typeof item.value === "string") return item.value.replace("T", " ");
  if (item.id === "moon_phase" && typeof item.value === "string") {
    const phase = item.value.trim();
    if (!phase) return t("fishing_today_general_unavailable");
    const key = phase.toLowerCase().replace(/\s+/g, "_");
    return t(`moon_phase_${key}`, { defaultValue: phase });
  }
  return t("fishing_today_general_state_unknown");
}

export default function EnvironmentalContextSection({ context, legacy, locale, t }) {
  // G7-approved legacy aquatic values remain useful, without inventing metadata.
  const legacyIds = new Set(["marine_sea_surface_temperature", "marine_sea_level_height",
    "freshwater_temperature", "river_discharge", "river_level"]);
  const items = Array.isArray(context?.items) ? context.items :
    (Array.isArray(legacy) ? legacy.filter(item => item && legacyIds.has(item.observationId)).map(item => ({
      id: item.observationId, value: item.value, units: item.units, status: "available",
      evidence: { provider: item.source, freshness: "unknown" }, affectsBiology: false,
    })) : []);
  const approved = items.filter(item => item && typeof CONTEXT_LABELS[item.id] === "string" && item.affectsBiology === false);
  if (!approved.length) return null;
  return (
    <section className="rp-general-context" aria-labelledby="general-context-title">
      <h5 id="general-context-title">{t("fishing_today_general_context_title")}</h5>
      <p>{t("fishing_today_general_context_separate")}</p>
      <dl>
        {approved.map(item => (
          <div key={item.id} data-context-status={item.status}>
            <dt>{t(`fishing_today_general_context_${CONTEXT_LABELS[item.id]}`)}</dt>
            <dd>
              <strong>{displayValue(item, locale, t)}</strong>
              {item.status === "historical" ? <small>{t("fishing_today_general_historical")}</small> : null}
              {item.evidence?.spatialApplicability === "regional_model" ?
                <small>{t("fishing_today_general_regional_model")}</small> : null}
              <details>
                <summary>{t("fishing_today_general_evidence_details")}</summary>
                <small>{typeof item.evidence?.provider === "string" ? item.evidence.provider : t("fishing_today_general_unavailable")}</small>
                {typeof item.evidence?.validTimeUtc === "string" ? <time dateTime={item.evidence.validTimeUtc}>{item.evidence.validTimeUtc}</time> : null}
                <small>{t(`fishing_today_general_freshness_${["fresh", "stale", "historical", "not_applicable"].includes(item.evidence?.freshness) ? item.evidence.freshness : "unknown"}`)}</small>
                {item.evidence?.layer && item.evidence.layer !== "not_applicable" ?
                  <small>{t(`fishing_today_general_layer_${["surface", "fixed_depth", "bulk_representative"].includes(item.evidence.layer) ? item.evidence.layer : "unknown"}`)}</small> : null}
              </details>
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
